import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import PushNotificationManager from '@/components/PushNotificationManager';
import { API, getAuthHeaders } from '../utils/api';

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true, headers: getAuthHeaders() });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? {...n, read: true} : n)
      );
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-slate-400">Loading notifications...</p>
        </div>
      </div>
    );
  }

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-['Manrope'] mb-2">Notifications</h1>
          <p className="text-slate-400">
            {unreadNotifications.length} unread notification{unreadNotifications.length !== 1 ? 's' : ''}
          </p>
        </div>
        <PushNotificationManager />
      </div>

      {/* Unread notifications */}
      {unreadNotifications.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold font-['Manrope'] text-slate-300">Unread</h2>
          {unreadNotifications.map((notif) => (
            <Card 
              key={notif.notification_id} 
              className="bg-[#121214] border-blue-500/30 p-6 hover:border-blue-500/50 transition-colors"
              data-testid={`notification-unread-${notif.notification_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex gap-4 flex-1">
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-medium mb-1">{notif.message}</p>
                    <p className="text-slate-500 text-sm font-mono">
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markAsRead(notif.notification_id)}
                    data-testid={`mark-read-${notif.notification_id}`}
                    className="border-zinc-700 hover:bg-zinc-800"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Mark as read
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/signals/${notif.signal_id}`)}
                    data-testid={`view-signal-${notif.notification_id}`}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    View Signal
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Read notifications */}
      {readNotifications.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold font-['Manrope'] text-slate-300">Read</h2>
          {readNotifications.map((notif) => (
            <Card 
              key={notif.notification_id} 
              className="bg-[#121214] border-zinc-800 p-6 opacity-60 hover:opacity-100 hover:border-zinc-700 transition-all"
              data-testid={`notification-read-${notif.notification_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex gap-4 flex-1">
                  <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-zinc-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-400 font-medium mb-1">{notif.message}</p>
                    <p className="text-slate-600 text-sm font-mono">
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/signals/${notif.signal_id}`)}
                  className="text-slate-500 hover:text-white"
                >
                  View Signal
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {notifications.length === 0 && (
        <Card className="bg-[#121214] border-zinc-800 p-12 text-center">
          <Bell className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg mb-2">No notifications yet</p>
          <p className="text-slate-500 text-sm">You'll be notified when new signals are posted</p>
        </Card>
      )}
    </div>
  );
}
